import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { listTenants, type ListTenantsResult, type Tenant } from '../api/tenants.js';
import { TenantWorkspaceUrl } from '../components/TenantWorkspaceUrl.js';
import { t } from '../i18n/es.js';

type TierKey = NonNullable<Tenant['tier']>;
type StatusKey = NonNullable<Tenant['status']>;

const TIER_LABELS: Record<TierKey, string> = {
  logical: t.tenant.tierLabels.logical,
  physical: t.tenant.tierLabels.physical,
};

const STATUS_LABELS: Record<StatusKey, string> = {
  active: 'Activos',
  suspended: 'Suspendidos',
  pending: 'Pendientes',
};

const STATUS_TONE: Record<StatusKey, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  suspended: 'bg-amber-100 text-amber-900',
  pending: 'bg-slate-100 text-slate-700',
};

export function Dashboard(): JSX.Element {
  const { data, isLoading, error } = useQuery<ListTenantsResult>({
    queryKey: ['tenants'],
    queryFn: () => listTenants({ limit: 100 }),
  });
  const tenants = useMemo(() => data?.items ?? [], [data]);

  const kpis = useMemo(() => {
    const byTier = new Map<TierKey, number>();
    const byStatus = new Map<StatusKey, number>();
    for (const tn of tenants) {
      if (tn.tier) byTier.set(tn.tier, (byTier.get(tn.tier) ?? 0) + 1);
      if (tn.status) byStatus.set(tn.status, (byStatus.get(tn.status) ?? 0) + 1);
    }
    return {
      total: tenants.length,
      byTier,
      byStatus,
    };
  }, [tenants]);

  const recent = useMemo(() => {
    return [...tenants]
      .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
      .slice(0, 8);
  }, [tenants]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Dashboard</h2>
        <Link
          to="/tenants/new"
          className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
        >
          {t.tenant.newBtn}
        </Link>
      </div>

      {isLoading && <p className="text-slate-500">{t.app.loading}</p>}
      {error && <p className="text-red-700">{(error as Error).message}</p>}

      {!isLoading && !error && (
        <>
          <section>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
              Resumen
            </h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <KpiTile label="Total tenants" value={kpis.total} accent="brand" />
              <KpiTile
                label={TIER_LABELS.logical}
                value={kpis.byTier.get('logical') ?? 0}
                accent="indigo"
              />
              <KpiTile
                label={TIER_LABELS.physical}
                value={kpis.byTier.get('physical') ?? 0}
                accent="violet"
              />
              <KpiTile
                label="Enterprise"
                value={0}
                accent="amber"
                badge={t.tenant.enterpriseSoon}
              />
            </div>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
              Estado
            </h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {(['active', 'pending', 'suspended'] as StatusKey[]).map((s) => (
                <div
                  key={s}
                  className={`rounded-lg border border-slate-200 bg-white p-4 shadow-sm`}
                >
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    {STATUS_LABELS[s]}
                  </div>
                  <div className="mt-1 text-2xl font-semibold">
                    {kpis.byStatus.get(s) ?? 0}
                  </div>
                  <span className={`mt-2 inline-block rounded px-2 py-0.5 text-xs ${STATUS_TONE[s]}`}>
                    {s}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Últimos tenants actualizados
              </h3>
              <Link to="/tenants" className="text-xs text-brand-600 hover:underline">
                Ver todos
              </Link>
            </div>
            {recent.length === 0 ? (
              <p className="text-slate-500">{t.tenant.empty}</p>
            ) : (
              <ul className="divide-y divide-slate-200 rounded border border-slate-200 bg-white">
                {recent.map((tenant) => (
                  <li key={tenant.id} className="p-3 hover:bg-slate-50">
                    <div className="flex items-center justify-between gap-3">
                      <Link to={`/tenants/${tenant.id}`} className="min-w-0 flex-1">
                        <div className="font-medium">{tenant.name}</div>
                        <div className="text-xs text-slate-500">
                          {tenant.slug} · {tenant.tier ?? '—'}
                        </div>
                        <div className="mt-1">
                          <TenantWorkspaceUrl slug={tenant.slug} compact />
                        </div>
                      </Link>
                      <div className="flex shrink-0 items-center gap-2">
                        {tenant.status && (
                          <span
                            className={`rounded px-2 py-0.5 text-xs ${STATUS_TONE[tenant.status]}`}
                          >
                            {tenant.status}
                          </span>
                        )}
                        {tenant.updatedAt && (
                          <span className="text-xs text-slate-400">
                            {new Date(tenant.updatedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
              Alarmas
            </h3>
            <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
              Pendiente: integración con CloudWatch alarms y EventBridge bus.
              Próxima iteración mostrará métricas en tiempo real.
            </div>
          </section>
        </>
      )}
    </div>
  );
}

interface KpiTileProps {
  label: string;
  value: number;
  accent: 'brand' | 'indigo' | 'violet' | 'amber';
  badge?: string;
}

function KpiTile({ label, value, accent, badge }: KpiTileProps): JSX.Element {
  const accentClass: Record<KpiTileProps['accent'], string> = {
    brand: 'border-brand-200 bg-brand-50',
    indigo: 'border-indigo-200 bg-indigo-50',
    violet: 'border-violet-200 bg-violet-50',
    amber: 'border-amber-200 bg-amber-50',
  };
  return (
    <div className={`rounded-lg border p-4 shadow-sm ${accentClass[accent]}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {badge && (
        <span className="mt-2 inline-block rounded bg-white px-2 py-0.5 text-xs text-slate-600">
          {badge}
        </span>
      )}
    </div>
  );
}
